import React, { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import {
  Container,
  Paper,
  Typography,
  Button,
  Box,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Divider,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import axios from "axios";

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  marginTop: theme.spacing(4),
  marginBottom: theme.spacing(4),
  background: "linear-gradient(135deg, #604221 0%, #8B4513 100%)",
  color: "white",
  borderRadius: theme.spacing(2),
}));

const PrizeCard = styled(Card)(({ theme }) => ({
  marginTop: theme.spacing(3),
  marginBottom: theme.spacing(3),
  background: "rgba(255, 255, 255, 0.1)",
  backdropFilter: "blur(10px)",
  border: "1px solid rgba(255, 255, 255, 0.2)",
  borderRadius: theme.spacing(2),
}));

const ClaimButton = styled(Button)(({ theme }) => ({
  background: "linear-gradient(45deg, #FE6B8B 30%, #FF8E53 90%)",
  border: 0,
  borderRadius: theme.spacing(3),
  boxShadow: "0 3px 5px 2px rgba(255, 105, 135, .3)",
  color: "white",
  height: 48,
  padding: "0 30px",
  fontSize: "1.1rem",
  fontWeight: "bold",
  "&:hover": {
    background: "linear-gradient(45deg, #FE6B8B 60%, #FF8E53 100%)",
    transform: "translateY(-2px)",
    boxShadow: "0 6px 10px 4px rgba(255, 105, 135, .3)",
  },
  "&:disabled": {
    background: "rgba(255, 255, 255, 0.3)",
    color: "rgba(255, 255, 255, 0.7)",
  },
}));

const ClaimGiveaway = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const email = searchParams.get("email");

  const [giveaway, setGiveaway] = useState(null);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    fetchGiveawayDetails();
  }, [id, email]);

  const fetchGiveawayDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${
          process.env.REACT_APP_API_URL
        }/giveaways/${id}/details?email=${encodeURIComponent(email)}`
      );
      console.log("response ==>", response);
      setGiveaway(response.data.giveaway);
    } catch (error) {
      setError(
        error.response?.data?.message || "Failed to load giveaway details"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClaim = async () => {
    try {
      setClaiming(true);
      setError("");

      const response = await axios.patch(`/api/giveaways/${id}/claim`, {
        email: email,
      });

      setSuccess(response.data.message);
      setGiveaway((prev) => ({
        ...prev,
        status: "claimed",
        claimed_date: new Date(),
      }));
    } catch (error) {
      setError(error.response?.data?.message || "Failed to claim giveaway");
    } finally {
      setClaiming(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md">
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
        >
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  if (error && !giveaway) {
    return (
      <Container maxWidth="md">
        <StyledPaper>
          <Alert
            severity="error"
            sx={{ mb: 2, backgroundColor: "rgba(255, 255, 255, 0.1)" }}
          >
            {error}
          </Alert>
        </StyledPaper>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <StyledPaper>
        <Typography
          variant="h3"
          component="h1"
          gutterBottom
          align="center"
          sx={{ fontWeight: "bold" }}
        >
          🎉 Congratulations! 🎉
        </Typography>

        <Typography variant="h5" align="center" sx={{ mb: 3, opacity: 0.9 }}>
          You've won our giveaway!
        </Typography>

        {giveaway && (
          <>
            <PrizeCard>
              <CardContent sx={{ color: "white" }}>
                <Typography
                  variant="h4"
                  component="h2"
                  gutterBottom
                  sx={{ fontWeight: "bold" }}
                >
                  {giveaway.title}
                </Typography>

                <Divider
                  sx={{ my: 2, backgroundColor: "rgba(255, 255, 255, 0.3)" }}
                />

                <Typography
                  variant="body1"
                  paragraph
                  sx={{ fontSize: "1.1rem", lineHeight: 1.6 }}
                >
                  {giveaway.description}
                </Typography>

                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1 }}>
                    🏆 Your Prize:
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{
                      color: "#FFFFFF",
                      fontWeight: "bold",
                      textShadow: "2px 2px 4px rgba(0,0,0,0.3)",
                    }}
                  >
                    {giveaway.prize}
                  </Typography>
                </Box>

                {giveaway.winner_selected_date && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>
                      Winner selected on:{" "}
                      {new Date(
                        giveaway.winner_selected_date
                      ).toLocaleDateString()}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </PrizeCard>

            {error && (
              <Alert
                severity="error"
                sx={{ mb: 2, backgroundColor: "rgba(255, 255, 255, 0.1)" }}
              >
                {error}
              </Alert>
            )}

            {success && (
              <Alert
                severity="success"
                sx={{ mb: 2, backgroundColor: "rgba(255, 255, 255, 0.1)" }}
              >
                {success}
              </Alert>
            )}

            <Box sx={{ textAlign: "center", mt: 4 }}>
              {giveaway.status === "claimed" ? (
                <>
                  <Typography variant="h6" sx={{ mb: 2, color: "#4CAF50" }}>
                    ✅ Prize Claimed Successfully!
                  </Typography>
                  {giveaway.claimed_date && (
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>
                      Claimed on:{" "}
                      {new Date(giveaway.claimed_date).toLocaleDateString()}
                    </Typography>
                  )}
                </>
              ) : giveaway.status === "winner_selected" ? (
                <ClaimButton
                  onClick={handleClaim}
                  disabled={claiming}
                  size="large"
                >
                  {claiming ? (
                    <>
                      <CircularProgress
                        size={24}
                        sx={{ mr: 1, color: "white" }}
                      />
                      Claiming...
                    </>
                  ) : (
                    "Claim Your Prize Now!"
                  )}
                </ClaimButton>
              ) : (
                <Typography variant="body1" sx={{ opacity: 0.8 }}>
                  This giveaway is not available for claiming.
                </Typography>
              )}
            </Box>

            <Box sx={{ mt: 4, textAlign: "center" }}>
              <Typography variant="body2" sx={{ opacity: 0.7 }}>
                Questions? Contact us at support@naturesperks.com
              </Typography>
            </Box>
          </>
        )}
      </StyledPaper>
    </Container>
  );
};

export default ClaimGiveaway;